<template>
  <app-bar title="Facility Schedule" :member-id="$route.params.memberId" />

  <v-sheet class="mx-3 mt-3 pa-3" rounded="xl" elevation="2">
    <div class="d-flex align-center justify-space-between flex-wrap gap-2">
      <div class="d-flex align-center">
        <v-btn
          icon
          variant="text"
          :color="ui.primaryColor"
          @click="navigateDate(-1)"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <div
          class="date-display px-2 py-1 rounded-lg"
          @click="showDatePicker = true"
          style="cursor: pointer"
        >
          <v-icon size="18" class="me-1" :color="ui.primaryColor"
            >mdi-calendar</v-icon
          >
          <span
            class="date-text font-weight-bold"
            :style="{ color: ui.primaryColor }"
            >{{ formattedDate }}</span
          >
        </div>
        <v-btn
          icon
          variant="text"
          :color="ui.primaryColor"
          @click="navigateDate(1)"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn
          variant="tonal"
          size="small"
          class="ms-2 font-weight-bold"
          :color="ui.primaryColor"
          @click="goToday"
        >
          Today
        </v-btn>
      </div>
      <div class="flex-grow-1 flex-sm-grow-0" style="min-width: 150px">
        <v-select
          v-model="branchFilter"
          :items="branchs"
          label="Branch"
          variant="outlined"
          density="compact"
          hide-details
          prepend-inner-icon="mdi-map-marker-outline"
          :color="ui.primaryColor"
        />
      </div>
    </div>
  </v-sheet>

  <!-- Date Picker Dialog -->
  <v-dialog v-model="showDatePicker" max-width="360">
    <v-card rounded="xl">
      <v-date-picker
        v-model="selectedDate"
        hide-header
        @update:model-value="showDatePicker = false"
      />
    </v-card>
  </v-dialog>

  <!-- Loading State -->
  <v-overlay
    :model-value="isLoading"
    class="align-center justify-center"
    contained
  >
    <v-progress-circular
      :color="ui.primaryColor || 'primary'"
      size="56"
      indeterminate
    />
  </v-overlay>

  <!-- Empty State -->
  <transition name="fade">
    <v-sheet
      v-if="!isLoading && facilityGroups.length === 0"
      class="ma-3 pa-10 text-center"
      rounded="xl"
      elevation="2"
    >
      <v-icon size="72" color="grey-lighten-1"
        >mdi-calendar-blank-outline</v-icon
      >
      <p class="empty-title mt-3">No bookings on this day</p>
      <p class="empty-subtitle">{{ formattedDate }}</p>
    </v-sheet>
  </transition>

  <!-- Schedule Card -->
  <transition name="fade">
    <v-sheet
      v-if="!isLoading && facilityGroups.length > 0"
      class="ma-3 pa-3"
      rounded="xl"
      elevation="2"
    >
      <div class="schedule-scroll-wrapper">
        <!-- Timeline Ruler -->
        <div class="timeline-ruler">
          <div class="ruler-label-spacer" />
          <div class="ruler-hours">
            <div
              v-for="hour in timelineHours"
              :key="hour"
              class="ruler-hour-cell"
              :style="{ width: hourWidth + 'px' }"
            >
              <span class="ruler-hour-text">{{ formatHour(hour) }}</span>
            </div>
          </div>
        </div>

        <!-- Facility Rows -->
        <div class="facility-list">
          <div
            v-for="group in facilityGroups"
            :key="group.facilityNo"
            class="facility-row"
          >
            <!-- Facility Label -->
            <div class="facility-label">
              <div
                class="facility-badge"
                :style="{
                  background:
                    ui.primaryColor ||
                    'linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)',
                  boxShadow: ui.primaryColor
                    ? `0 2px 8px ${ui.primaryColor}40`
                    : '0 2px 8px rgba(21, 101, 192, 0.25)',
                }"
              >
                <span class="facility-badge-no">No.{{ group.facilityNo }}</span>
              </div>
            </div>

            <!-- Booking Track -->
            <div class="facility-track">
              <!-- Hour grid lines -->
              <div class="hour-grid">
                <div
                  v-for="hour in timelineHours"
                  :key="hour"
                  class="hour-cell"
                  :style="{ width: hourWidth + 'px' }"
                />
              </div>

              <!-- Booking blocks -->
              <div
                v-for="booking in group.bookings"
                :key="booking.id + '-' + booking.slotIdx"
                class="booking-block"
                :style="getBlockStyle(booking)"
                @click="onBookingClick(booking)"
              >
                <div class="booking-block-inner">
                  <div class="booking-time">
                    {{ booking.startFormatted }} – {{ booking.endFormatted }}
                  </div>
                  <div class="booking-resource">{{ booking.resource }}</div>
                </div>
              </div>

              <!-- Current time indicator (only for today) -->
              <div
                v-if="isToday"
                class="current-time-line"
                :style="{
                  left: currentTimeLeft + 'px',
                  background: ui.secondaryColor || '#e53935',
                }"
              />
            </div>
          </div>
        </div>
      </div>
    </v-sheet>
  </transition>

  <!-- Booking Detail Bottom Sheet -->
  <v-bottom-sheet v-model="isDetailShown" inset>
    <v-card class="detail-sheet pa-5" rounded="t-xl">
      <div class="d-flex align-start">
        <div class="flex-grow-1 text-start">
          <div class="detail-facility-no" :style="{ color: ui.primaryColor }">
            Facility No. {{ detailBooking.facilityNo }}
          </div>
          <div class="detail-resource">
            <v-icon size="16" class="me-1">mdi-land-plots</v-icon>
            {{ detailBooking.resource }}
          </div>
          <div class="detail-time mt-2">
            <v-icon size="16" class="me-1">mdi-clock-outline</v-icon>
            {{ detailBooking.startFormatted }} –
            {{ detailBooking.endFormatted }}
          </div>
          <div class="detail-date mt-1">
            <v-icon size="16" class="me-1">mdi-calendar</v-icon>
            {{ formattedDate }}
          </div>
        </div>
        <v-btn icon variant="text" @click="isDetailShown = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-card>
  </v-bottom-sheet>
</template>

<script setup>
import AppBar from "@/components/AppBar.vue";
import BackendConnector from "@/util/BackendConnector";
import axios from "axios";
import moment from "moment";
import {
  ref,
  reactive,
  computed,
  watch,
  onMounted,
  onUnmounted,
  defineProps,
} from "vue";
import { useRoute } from "vue-router";

const props = defineProps({
  ui: {
    type: Object,
    default() {
      return {};
    },
  },
});

const route = useRoute();

// ─── State ────────────────────────────────────────────────────────────────────
const selectedDate = ref(new Date());
const branchFilter = ref("all");
const branchs = reactive([{ title: "All", value: "all" }]);
const isLoading = ref(false);
const showDatePicker = ref(false);

const rawBookings = ref([]);

const isDetailShown = ref(false);
const detailBooking = reactive({});

// ─── Timeline Config ───────────────────────────────────────────────────────────
const TIME_START = 6; // 06:00
const TIME_END = 23; // 23:00
const hourWidth = 80; // px per hour

const timelineHours = computed(() => {
  const hours = [];
  for (let h = TIME_START; h <= TIME_END; h++) hours.push(h);
  return hours;
});

// ─── Computed ─────────────────────────────────────────────────────────────────
const formattedDate = computed(() =>
  moment(selectedDate.value).format("ddd, D MMM YYYY")
);

const isToday = computed(() =>
  moment(selectedDate.value).isSame(moment(), "day")
);

const _tick = ref(0);

const currentTimeLeft = computed(() => {
  // Access reactive variable to trigger recalculation
  _tick.value;
  const now = moment();
  const minutesFromStart = (now.hours() - TIME_START) * 60 + now.minutes();
  return (minutesFromStart / 60) * hourWidth;
});

const facilityGroups = computed(() => {
  const map = new Map();

  rawBookings.value.forEach((booking) => {
    const slots = booking.start_time || [];
    const facilities = Array.isArray(booking.occupation_facility)
      ? booking.occupation_facility
      : booking.occupation_facility
      ? [booking.occupation_facility]
      : [];

    facilities.forEach((facilityNo) => {
      slots.forEach((_, idx) => {
        const sTime = moment(booking.start_time[idx], "HH:mm:ss");
        const eTime = moment(booking.end_time[idx], "HH:mm:ss");

        const entry = {
          id: booking.id,
          slotIdx: `${facilityNo}-${idx}`,
          facilityNo: facilityNo,
          attendee: booking.attendee,
          resource: booking.resource,
          startFormatted: sTime.format("HH:mm"),
          endFormatted: eTime.format("HH:mm"),
          startMinutes: sTime.hours() * 60 + sTime.minutes(),
          endMinutes: eTime.hours() * 60 + eTime.minutes(),
        };

        if (!map.has(facilityNo)) map.set(facilityNo, []);
        map.get(facilityNo).push(entry);
      });
    });
  });

  return Array.from(map.entries())
    .sort((a, b) => {
      const na = parseInt(a[0]) || 0;
      const nb = parseInt(b[0]) || 0;
      return na - nb;
    })
    .map(([facilityNo, bookings]) => ({ facilityNo, bookings }));
});

// ─── Methods ──────────────────────────────────────────────────────────────────
function formatHour(hour) {
  return moment({ hour }).format("HH:mm");
}

function getBlockStyle(booking) {
  const startOffset = booking.startMinutes - TIME_START * 60;
  const duration = booking.endMinutes - booking.startMinutes;

  const left = (startOffset / 60) * hourWidth;
  const width = Math.max((duration / 60) * hourWidth - 4, 20);

  const primaryColor = props.ui.primaryColor || "#1565c0";
  const isHex = /^#[0-9A-F]{6}$/i.test(primaryColor);
  const bgColor = isHex ? `${primaryColor}1a` : "rgba(21, 101, 192, 0.1)";
  const borderColor = primaryColor;
  const textColor = primaryColor;

  return {
    left: left + "px",
    width: width + "px",
    backgroundColor: bgColor,
    borderColor: borderColor,
    color: textColor,
  };
}

function onBookingClick(booking) {
  Object.assign(detailBooking, booking);
  isDetailShown.value = true;
}

function navigateDate(delta) {
  const d = moment(selectedDate.value).add(delta, "days").toDate();
  selectedDate.value = d;
}

function goToday() {
  selectedDate.value = new Date();
}

async function loadSchedule() {
  isLoading.value = true;
  const dateStr = moment(selectedDate.value).format("YYYY-MM-DD");
  try {
    const response = await axios({
      method: "GET",
      url: `${process.env.VUE_APP_BACKEND_URL}/api/facility_booking/calendar_no_login`,
      params: {
        start_date: dateStr,
        end_date: dateStr,
        branch: branchFilter.value,
        shop_id: route.params.shopId,
      },
    });
    rawBookings.value = response.data || [];
  } catch {
    rawBookings.value = [];
  } finally {
    isLoading.value = false;
  }
}

// ─── Watchers ─────────────────────────────────────────────────────────────────
watch([selectedDate, branchFilter], () => {
  loadSchedule();
});

// ─── Ticker ───────────────────────────────────────────────────────────────────
let ticker = null;

onMounted(async () => {
  await BackendConnector.loadBranchs(route.params.shopId)
    .then((branchesList) => {
      branchesList.forEach((b) => {
        branchs.push({ title: b.title, value: b.value });
      });
    })
    .catch(() => {});

  loadSchedule();

  ticker = setInterval(() => {
    _tick.value++;
  }, 60_000);
});

onUnmounted(() => {
  clearInterval(ticker);
});
</script>

<style scoped>
/* ── Schedule scroll wrapper ─────────────────────────────────────── */
.schedule-scroll-wrapper {
  overflow-x: auto;
  width: 100%;
  position: relative;
  -webkit-overflow-scrolling: touch;
}

/* ── Timeline Ruler ───────────────────────────────────────────────── */
.timeline-ruler {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e0e6ed;
  padding-bottom: 8px;
  margin-bottom: 8px;
}
.ruler-label-spacer {
  width: 88px;
  flex-shrink: 0;
}
.ruler-hours {
  display: flex;
  overflow: visible;
}
.ruler-hour-cell {
  flex-shrink: 0;
  text-align: left;
  padding-left: 4px;
}
.ruler-hour-text {
  font-size: 0.72rem;
  font-weight: 600;
  color: #90a4ae;
  letter-spacing: 0.03em;
}

/* ── Facility Row ─────────────────────────────────────────────────── */
.facility-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.facility-row {
  display: flex;
  align-items: center;
  min-height: 60px;
}

/* Facility Label */
.facility-label {
  width: 88px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: 0 6px;
}
.facility-badge {
  border-radius: 10px;
  padding: 6px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 68px;
  transition: all 0.3s ease;
}
.facility-badge-no {
  font-size: 0.78rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

/* Facility Track */
.facility-track {
  flex: 1;
  position: relative;
  height: 52px;
  overflow: visible;
}

/* Hour grid lines */
.hour-grid {
  display: flex;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}
.hour-cell {
  flex-shrink: 0;
  border-left: 1px solid #e0e6ed;
  height: 100%;
  box-sizing: border-box;
}

/* Booking Block */
.booking-block {
  position: absolute;
  top: 4px;
  height: 44px;
  border-radius: 8px;
  border: 1.5px solid;
  cursor: pointer;
  overflow: hidden;
  transition: box-shadow 0.18s, transform 0.15s;
  z-index: 2;
}
.booking-block:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}
.booking-block-inner {
  padding: 3px 7px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
}
.booking-time {
  font-size: 0.7rem;
  font-weight: 700;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.booking-name {
  font-size: 0.72rem;
  font-weight: 600;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.booking-resource {
  font-size: 0.65rem;
  color: #546e7a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Current time indicator */
.current-time-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  border-radius: 2px;
  z-index: 4;
}
.current-time-line::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: inherit;
}

/* ── Empty State ──────────────────────────────────────────────────── */
.empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #78909c;
}
.empty-subtitle {
  font-size: 0.85rem;
  color: #b0bec5;
}

/* ── Detail Sheet ─────────────────────────────────────────────────── */
.detail-sheet {
  border-radius: 16px 16px 0 0 !important;
}
.detail-facility-no {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 4px;
}
.detail-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #263238;
  margin: 0 0 4px;
}
.detail-resource,
.detail-time,
.detail-date {
  display: flex;
  align-items: center;
  font-size: 0.88rem;
  color: #546e7a;
}

/* ── Transitions ──────────────────────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
